import { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

import { MedicationPeekList } from '../components/MedicationPeekList';
import { useUnifiedMedicationsQuery } from '../store/api';
import { mapUnifiedToVM } from '../vm/mapUnifiedToVM';

interface UnifiedMedicationPeekProps {
  patientId: string;
  /** Opens the full edit modal (the peek itself is read-only). */
  onEdit?: () => void;
}

/**
 * Data glue for the context-bar medication peek. Reads the same unified
 * history (and RTK Query cache entry) as the patient-page card, so the peek,
 * the card, and the gated edit modal always agree. Mounted lazily — the
 * popover only renders its children while open, so the query fires on first
 * hover, not on page load.
 */
export const UnifiedMedicationPeek: React.FC<UnifiedMedicationPeekProps> = ({
  patientId,
  onEdit,
}) => {
  const { data, isLoading, isError } = useUnifiedMedicationsQuery({ patientId });

  const workspace = useMemo(
    () => (data ? mapUnifiedToVM(data).workspace : undefined),
    [data],
  );

  if (isLoading || !workspace) {
    return (
      <Box sx={{ minWidth: 400, maxWidth: 600, p: 2 }}>
        {isError ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            Medications could not be loaded.
          </Typography>
        ) : (
          [0, 1, 2].map((row) => (
            <Skeleton key={row} variant="rounded" height={28} sx={{ my: 0.75 }} />
          ))
        )}
      </Box>
    );
  }

  return <MedicationPeekList workspace={workspace} onEdit={onEdit} />;
};
